import { PublicClientApplication } from '@azure/msal-browser';
import authService from './auth';
import { getRuntimeEnvValue } from '../config/runtimeConfig';

function getEnvValue(key: string): string | undefined {
  return getRuntimeEnvValue(key);
}

function requireEnv(name: string): string {
  const value = (getEnvValue(name) || '').trim();
  if (!value) {
    throw new Error(`Missing ${name}`);
  }
  return value;
}

function getFrontendClientId(): string {
  return requireEnv('VITE_ENTRA_CLIENT_ID');
}

function getTenantId(): string {
  return requireEnv('VITE_ENTRA_TENANT_ID');
}

function getRedirectUri(): string {
  return requireEnv('VITE_ENTRA_REDIRECT_URI');
}

function getApiScope(): string {
  const configured = (getEnvValue('VITE_ENTRA_API_SCOPE') || '').trim();
  if (configured) {
    return configured;
  }

  const backendClientId = (getEnvValue('VITE_ENTRA_BACKEND_CLIENT_ID') || '').trim();
  if (!backendClientId) {
    throw new Error('Missing VITE_ENTRA_API_SCOPE or VITE_ENTRA_BACKEND_CLIENT_ID');
  }

  return `api://${backendClientId}/access_as_user`;
}

export function hasPendingEntraRedirectResponse(): boolean {
  const search = window.location.search || '';
  const hash = window.location.hash || '';

  const searchHasAuth = /[?&](code|error|state)=/i.test(search);
  const hashHasAuth = /[#&](code|error|state)=/i.test(hash);

  return searchHasAuth || hashHasAuth;
}

let app: PublicClientApplication | null = null;

function getMsalApp(): PublicClientApplication {
  if (app) return app;

  app = new PublicClientApplication({
    auth: {
      clientId: getFrontendClientId(),
      authority: `https://login.microsoftonline.com/${getTenantId()}`,
      redirectUri: getRedirectUri(),
      // Process redirect on the configured redirect URI before app routing.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });

  return app;
}

/**
 * Called on every page load. If Microsoft redirected back with an auth result,
 * exchange the token with the backend and return true. Returns false if no
 * redirect was in progress.
 */
export async function handleEntraRedirect(): Promise<boolean> {
  const msalApp = getMsalApp();
  await msalApp.initialize();

  const redirectResult = await msalApp.handleRedirectPromise();
  const account = redirectResult?.account || msalApp.getActiveAccount() || msalApp.getAllAccounts()[0] || null;
  if (!account) {
    return false;
  }

  msalApp.setActiveAccount(account);

  let accessToken = redirectResult?.accessToken;
  if (!accessToken) {
    const silentAccessToken = await acquireEntraAccessTokenSilent();
    if (silentAccessToken) {
      accessToken = silentAccessToken;
    }
  }

  if (!accessToken) {
    throw new Error('Entra redirect completed without an access token. Verify API scope and app registration settings.');
  }

  await authService.loginWithEntra({ access_token: accessToken });
  return true;
}

export async function acquireEntraAccessTokenSilent(): Promise<string | null> {
  const msalApp = getMsalApp();
  await msalApp.initialize();

  const account = msalApp.getActiveAccount() || msalApp.getAllAccounts()[0] || null;
  if (!account) {
    return null;
  }

  msalApp.setActiveAccount(account);

  try {
    const tokenResult = await msalApp.acquireTokenSilent({
      account,
      scopes: [getApiScope()],
    });
    return tokenResult.accessToken || null;
  } catch {
    return null;
  }
}

/**
 * Initiates the Entra login redirect flow. The page will navigate away to
 * Microsoft — result is picked up on return via handleEntraRedirect().
 */
export async function signInWithEntra(): Promise<void> {
  const msalApp = getMsalApp();
  await msalApp.initialize();

  await msalApp.loginRedirect({
    scopes: ['openid', 'profile', 'email', getApiScope()],
    redirectUri: getRedirectUri(),
    prompt: 'select_account',
  });
  // Page navigates away — code below this line will not execute.
}
